/**
 * Stage 4c: Article Finalization — context-based version using StagePanel.
 *
 * Reads all section drafts, refines for flow and consistency,
 * assembles into full markdown. LLM stage.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const sections = document?.article?.sections || [];
  const drafted = sections.filter((s) => s.body);
  const total = sections.length;
  const wordCount = document?.targeting?.competitors?.analysis?.target_word_count;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-gray-500 mb-1">
          Sections drafted: {drafted.length} / {total}
        </div>
        {drafted.length < total && (
          <div className="text-xs text-amber-600">
            {total - drafted.length} section{total - drafted.length > 1 ? 's' : ''} not yet drafted.
            Run Stage 4b first.
          </div>
        )}
      </div>
      {wordCount != null && (
        <div className="text-xs text-gray-500">
          Target word count: {wordCount}
        </div>
      )}
      {document?.article?.seo && (
        <div className="text-xs text-gray-500">
          SEO: {document.article.seo.meta_title} | {document.article.seo.slug}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const markdown = doc.article?.markdown;
  if (!markdown) {
    return <div className="text-sm text-gray-500">No finalized article yet.</div>;
  }
  const wordCount = markdown.split(/\s+/).filter(Boolean).length;
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="text-xs font-medium text-gray-700">Finalized article</div>
        <div className="text-xs text-gray-400">{wordCount} words</div>
      </div>
      <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-3 max-h-[500px] overflow-y-auto">
        {markdown}
      </pre>
    </div>
  );
}

export function Stage4cFinalizeV2() {
  const { document } = useArticleContext();
  const sections = document?.article?.sections || [];
  const allDrafted = sections.length > 0 && sections.every((s) => s.body);

  return (
    <StagePanel
      stageId="stage_4c_research_finalize"
      isLlmStage
      title="Stage 4c: Article Finalization"
      description="Refines all section drafts for flow and consistency, then assembles the full article."
      runDisabled={!allDrafted}
      runDisabledReason={!allDrafted ? 'All sections must be drafted first (Stage 4b)' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
